import React, { useMemo, useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import MuxPlayer from '@mux/mux-player-react'
import EventCard from '@/components/EventCard'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import Checkbox from '@/components/ui/checkbox_custom'
import { SlidersHorizontal, Search, X, Play, ArrowRight } from 'lucide-react'
import { sanity, urlFor } from '../lib/sanity'

const EVENTS_QUERY = `*[_type == "event"] | order(startDateTime asc) {
  _id,
  title,
  "slug": slug.current,

  "type": coalesce(typeOption->title, type),
  "typeValue": coalesce(typeOption->value, type),

  "date": coalesce(dateOption->title, date),
  "dateValue": coalesce(dateOption->value, date),

  startDateTime,
  endDateTime,
  city,

  "region": coalesce(regionOption->title, region),
  "regionValue": coalesce(regionOption->value, region),

  organiser,
  featured,
  image
}`

const HOME_QUERY = `*[_type == "homePage"][0]{
  label,
  title,
  intro,
  heroImage,
  quickFilters[]{
    label,
    "type": option->kind,
    "value": option->value,
    "fallbackLabel": option->title,
    "active": coalesce(option->active, true)
  }
}`

/* const HOME_QUERY = `*[_type == "homePage"][0]{
  label,
  title,
  intro,
  heroImage,
  quickFilters[]{
    label,
    option,
    "optionId": option._ref,
    "optionType": option->_type,
    "optionTitle": option->title,
    "optionKind": option->kind,
    "optionValue": option->value,
    "optionActive": option->active
  }
}` */

const EVENT_FILTER_OPTIONS_QUERY = `*[
  _type == "eventFilterOption" &&
  coalesce(active, true) == true
] | order(kind asc, title asc) {
  _id,
  kind,
  title,
  value,
  timeZone
}`

const TEST_FILTER_OPTION_QUERY = `*[
  _id == "eventFilterOption.date.herdenking-29-juni"
][0]{
  _id,
  _type,
  title,
  kind,
  value,
  active
}`

/* const types = [
  'Film',
  'Herdenking',
  'Festival',
  'Lezing',
  'Kennisdeling',
  'Tentoonstelling',
  'Muziek',
  'Workshops',
  'Debat',
  'Dialoog',
  'Interactief',
  'Boekpresentatie',
  'Presentatie',
  'Voorstelling',
  'Mode',
  'Onderwijs',
  'Kinderen',
  'Bijeenkomst',
  'Anders',
]

const dates = [
  'Herdenking - 29 juni',
  'Herdenking - 30 juni',
  'Nationale Herdenking - 1 juli',
  'Dia di Tula - 17 aug',
  'Dag der Inheemse Volken - 9 aug',
  'Andere gelegenheid / datum',
]

const locations = [
  'Nederland',
  'Aruba',
  'Bonaire',
  'Curaçao',
  'Saba',
  'Sint Eustatius',
  'Sint Maarten',
  'Suriname',
] */

/* const quickFilters = [
  { label: '29 juni', type: 'date', value: 'Herdenking - 29 juni' },
  { label: '30 juni', type: 'date', value: 'Herdenking - 30 juni' },
  { label: '1 juli', type: 'date', value: 'Nationale Herdenking - 1 juli' },
  { label: 'Dia di Tula', type: 'date', value: 'Dia di Tula - 17 aug' },
  { label: 'Kinderen', type: 'type', value: 'Kinderen' },
  { label: 'Muziek', type: 'type', value: 'Muziek' },
  { label: 'Curaçao', type: 'location', value: 'Curaçao' },
] */

// reels

const placeholderImage = '/assets/images/placeholderEventImage.jpg'

function getMuxThumbnail(playbackId) {
  if (!playbackId) return placeholderImage

  return `https://image.mux.com/${playbackId}/thumbnail.jpg?time=1`
}

/* const featuredVideo = {
  title: 'In gesprek over herdenken en vieren',
  description:
    'Een korte reel-interview over wat herdenken vandaag betekent.',
  slug: 'in-gesprek-over-herdenken-en-vieren',
  image: '/assets/images/placeholderEventImage.jpg',
} */

const FEATURED_VIDEO_QUERY = `*[
  _type == "video" &&
  featured == true &&
  defined(slug.current) &&
  videoFile.asset->status == "ready"
] | order(_createdAt desc)[0]{
  _id,
  title,
  description,
  "slug": slug.current,
  "playbackId": coalesce(
    videoFile.asset->playbackId,
    videoFile.asset->data.playback_ids[0].id
  )
}`

function FilterGroup({title, options, selected, setSelected}) {
  function toggle(value) {
    setSelected((current) =>
      current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value]
    )
  }

  return (
    <div>
      <h3 className="mb-4 text-lg font-bold text-[#5757fd]">{title}</h3>

      <div className="space-y-3">
        {options.map((option) => (
          <label
            key={option.value}
            className="flex cursor-pointer items-center gap-3 text-[#5757fd]"
          >
            <Checkbox
              checked={selected.includes(option.value)}
              onChange={() => toggle(option.value)}
            />
            <span className="text-sm font-medium">{option.title}</span>
          </label>
        ))}
      </div>
    </div>
  )
}

function getTodayKey() {
  const today = new Date()

  const year = today.getFullYear()
  const month = String(today.getMonth() + 1).padStart(2, '0')
  const day = String(today.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

function isTodayOrFutureEvent(event) {
  const dateSource = event.endDateTime || event.startDateTime

  if (!dateSource) return false

  const eventDateKey = String(dateSource).slice(0, 10)
  const todayKey = getTodayKey()

  return eventDateKey >= todayKey
}

function FeaturedVideoBlock({ video }) {
  if (!video) return null

  return (
    <div className="relative flex min-h-[420px] items-center justify-center overflow-hidden bg-[#5757fd] p-6 md:min-h-full md:p-10">
      <div className="absolute right-[-70px] top-[-70px] h-96 w-96 rounded-full bg-white/15" />
      <div className="absolute bottom-[-90px] left-[-80px] h-96 w-96 rounded-full bg-white/10" />

      <div className="relative z-10 w-full max-w-[280px] md:max-w-[320px]">
        <div className="mb-4 flex items-center justify-between gap-3 text-white">
          <span className="rounded-full  border-white/40 px-4 py-2 text-4xl font-sans_bold tracking-wide">
            Videos
          </span>

          <Link
            to="/videos"
            className="text-sm font-sans_bold underline-offset-4 hover:underline text-white"
          >
            <div className='flex text-center'>Bekijk alles <ArrowRight className='w-4 h-4 ml-2' /></div>
          </Link>
        </div>

        <Link
          to={`/videos/${video.slug}`}
          className="group block"
          aria-label={`Bekijk video: ${video.title}`}
        >
          <div className="relative aspect-[9/16] overflow-hidden rounded-[2rem] bg-white shadow-[0_24px_70px_rgba(0,0,0,0.25)] transition-transform duration-500 group-hover:-rotate-2 group-hover:scale-[1.03]">
            {/* <img
              src={getMuxThumbnail(video.playbackId)}
              alt={video.title}
              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
            />

            <div className="absolute inset-0 bg-gradient-to-t from-[#5757fd]/50 via-transparent to-transparent" />

            <div className="absolute left-4 top-4 flex h-12 w-12 items-center justify-center rounded-full bg-white text-[#5757fd] shadow-sm transition-transform duration-300 group-hover:scale-110">
              <Play className="ml-0.5 h-5 w-5 fill-current" />
            </div> */}

{video.playbackId ? (
  <MuxPlayer
  playbackId={video.playbackId}
  streamType="on-demand"
  autoPlay
  muted
  loop
  playsInline
  controls={false}
  className="h-full w-full"
  style={{
    width: '100%',
    height: '100%',
    '--media-object-fit': 'cover',
    '--media-object-position': 'center',
    '--controls': 'none',
    '--play-button': 'none',
    '--center-controls': 'none',
    '--bottom-controls': 'none',
    '--top-controls': 'none',
  }}
/>
) : (
  <img
    src={getMuxThumbnail(video.playbackId)}
    alt={video.title}
    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
  />
)}

<div className="absolute inset-0 bg-gradient-to-t from-[#5757fd]/50 via-transparent to-transparent pointer-events-none" />

            <div className="absolute bottom-4 left-4 right-4">
              <div className="w-fit max-w-full rounded-2xl bg-white px-4 py-3 text-left text-[#5757fd] shadow-sm">
                <p className="text-sm font-sans_bold leading-tight">
                  {video.title}
                </p>
              </div>
            </div>
          </div>
        </Link>
      </div>
    </div>
  )
}

function HomePage() {
  const [home, setHome] = useState(null)
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)

  const [search, setSearch] = useState('')
  const [selectedTypes, setSelectedTypes] = useState([])
  const [selectedDates, setSelectedDates] = useState([])
  const [selectedLocations, setSelectedLocations] = useState([])
  const [featuredVideo, setFeaturedVideo] = useState(null)
  const [filterOptions, setFilterOptions] = useState([])

  const calendarRef = useRef(null)

  const types = useMemo(
    () => filterOptions.filter((option) => option.kind === 'type'),
    [filterOptions]
  )
  
  const dates = useMemo(
    () => filterOptions.filter((option) => option.kind === 'date'),
    [filterOptions]
  )
  
  const locations = useMemo(
    () => filterOptions.filter((option) => option.kind === 'location'),
    [filterOptions]
  )
  
  const quickFilters = useMemo(() => {
    return (home?.quickFilters || [])
      .filter((filter) => filter?.active !== false)
      .filter((filter) => filter?.type && filter?.value)
      .map((filter) => ({
        label: filter.label || filter.fallbackLabel,
        type: filter.type,
        value: filter.value,
      }))
  }, [home])

  useEffect(() => {
    Promise.all([
      sanity.fetch(HOME_QUERY),
      sanity.fetch(EVENTS_QUERY),
      sanity.fetch(FEATURED_VIDEO_QUERY),
      sanity.fetch(EVENT_FILTER_OPTIONS_QUERY),
    ])
      .then(([homeData, eventData, featuredVideoData, filterOptionData]) => {
    
        setHome(homeData)
        setEvents(eventData || [])
        setFeaturedVideo(featuredVideoData)
        setFilterOptions(filterOptionData || [])
        setLoading(false)
      })
      .catch((error) => {
        console.error(error)
        setLoading(false)
      })
  }, [])

  useEffect(() => {
    const hasActiveFilters =
      search ||
      selectedTypes.length > 0 ||
      selectedDates.length > 0 ||
      selectedLocations.length > 0

    if (hasActiveFilters && calendarRef.current) {
      calendarRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    }
  }, [search, selectedTypes, selectedDates, selectedLocations])

  const regularEvents = useMemo(() => {
    return events.filter((event) => {
      if (!isTodayOrFutureEvent(event)) return false
  
      const searchValue = search.trim().toLowerCase()
  
      const searchableText = [
        event.title,
        event.organiser,
        event.type,
        event.date,
        event.city,
        event.region,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
  
      const matchesSearch =
        !searchValue || searchableText.includes(searchValue)
  
      const matchesType =
        selectedTypes.length === 0 || selectedTypes.includes(event.typeValue)
  
      const matchesDate =
        selectedDates.length === 0 || selectedDates.includes(event.dateValue)
  
      const matchesLocation =
        selectedLocations.length === 0 ||
        selectedLocations.includes(event.regionValue)
  
      return matchesSearch && matchesType && matchesDate && matchesLocation
    })
  }, [events, search, selectedTypes, selectedDates, selectedLocations])

  const featuredEvents = useMemo(() => {
    return events.filter((event) => event.featured)
  }, [events])

  function getFilterLabel(value) {
    return (
      filterOptions.find((option) => option.value === value)?.title || value
    )
  }
  
  function clearFilters() {
    setSearch('')
    setSelectedTypes([])
    setSelectedDates([])
    setSelectedLocations([])
  }

  function toggleQuickFilter(filter) {
    if (filter.type === 'type') {
      setSelectedTypes((current) =>
        current.includes(filter.value)
          ? current.filter((item) => item !== filter.value)
          : [...current, filter.value]
      )
    }

    if (filter.type === 'date') {
      setSelectedDates((current) =>
        current.includes(filter.value)
          ? current.filter((item) => item !== filter.value)
          : [...current, filter.value]
      )
    }

    if (filter.type === 'location') {
      setSelectedLocations((current) =>
        current.includes(filter.value)
          ? current.filter((item) => item !== filter.value)
          : [...current, filter.value]
      )
    }
  }

  function isQuickFilterActive(filter) {
    if (filter.type === 'type') return selectedTypes.includes(filter.value)
    if (filter.type === 'date') return selectedDates.includes(filter.value)
    if (filter.type === 'location') return selectedLocations.includes(filter.value)
    return false
  }

  const hasActiveFilters =
    search ||
    selectedTypes.length > 0 ||
    selectedDates.length > 0 ||
    selectedLocations.length > 0

  if (loading) return null

  return (
    <main className="min-h-screen px-4 pb-20 pt-0 text-[#5757fd] md:px-8">
      <section className="mb-20">
        <div className="relative overflow-hidden rounded-[2rem] bg-white">
          <div className="grid min-h-[560px] md:grid-cols-[0.9fr_1.1fr]">
            <div className="relative z-10 flex flex-col justify-between p-6 md:p-10 lg:p-14">
              <div>
                {home?.label && (
                  <p className="mb-6 w-fit rounded-full bg-[#f0efff] px-4 py-2 text-sm font-bold">
                    {home.label}
                  </p>
                )}

                {home?.title && (
                  <h1 className="max-w-2xl font-sans_bold text-5xl tracking-tight md:text-7xl whitespace-pre-line">
                    {home.title}
                  </h1>
                )}

                {home?.intro && (
                  <p className="mt-8 max-w-xl font-sans text-lg leading-tight md:text-xl whitespace-pre-line">
                    {home.intro}
                  </p>
                )}
              </div>
            </div>

            {/*             {home?.heroImage && (
              <div className="relative min-h-[320px] max-h-[600px] overflow-hidden md:min-h-full">
                <img
                  src={urlFor(home.heroImage).width(1200).url()}
                  alt={home.heroImage?.alt || home?.title || 'Hero image'}
                  className="h-full w-full object-cover"
                />

                <div className="absolute inset-0 bg-gradient-to-t from-[#5757fd]/30 via-transparent to-transparent md:bg-gradient-to-r md:from-white/20 md:to-transparent" />
              </div>
            )} */}
            <FeaturedVideoBlock video={featuredVideo} />
          </div>
        </div>
      </section>

      {featuredEvents.length > 0 && (
        <section className="mb-4">
          <div className="mb-6 flex items-end justify-between gap-4">
            <h1 className="text-4xl font-medium font-sans px-2 tracking-tight md:text-5xl">
              Uitgelicht
            </h1>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            {featuredEvents.map((event) => (
              <EventCard key={event._id} event={event} featured />
            ))}
          </div>
        </section>
      )}

      <section>
        <div
          id="programma"
          ref={calendarRef}
          className="scroll-mt-12 mx-auto py-24 flex max-w-3xl flex-col items-center justify-center"
        >
          <h2 className="font-sans_bold py-4">Ontdek het programma</h2>

          <div className="max-w-xl flex w-full items-center gap-3 rounded-full bg-[#f3f2ff] px-5 py-4">
            <Search className="h-5 w-5 shrink-0 text-[#5757fd]" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Zoek op evenement, stad of organisator"
              className="border-none bg-transparent p-0 text-[#5757fd] shadow-none outline-none placeholder:text-[#5757fd] focus:border-none focus:outline-none focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0"
            />
          </div>

          <div className="mt-5 flex flex-wrap justify-center gap-2">
            {quickFilters.map((filter) => {
              const active = isQuickFilterActive(filter)

              return (
                <button
                  key={`${filter.type}-${filter.value}`}
                  onClick={() => toggleQuickFilter(filter)}
                  className={`rounded-full px-8 py-4 text-sm font-sans_bold transition-all duration-300 ${active
                      ? 'bg-[#5757fd] text-white'
                      : 'bg-[#5757fd]/10 text-[#5757fd] hover:bg-[#5757fd] hover:text-white'
                    }`}
                >
                  {filter.label}
                </button>
              )
            })}
          </div>
        </div>

        <div className="mb-6 flex items-center justify-between gap-4">
          <h2 className="text-4xl font-sans font-medium px-2 tracking-tight md:text-5xl">
            Programma
          </h2>

          <Sheet>
            <SheetTrigger asChild>
              <Button className="font-sans rounded-full bg-white px-8 text-[#5757fd] text-lg hover:bg-white/90">
                <SlidersHorizontal className="w-4 mr-2" /> filters
              </Button>
            </SheetTrigger>

            <SheetContent className="overflow-y-auto bg-white">
              <SheetHeader>
                <SheetTitle className="text-3xl text-[#5757fd]">
                  Filters
                </SheetTitle>
              </SheetHeader>

              <div className="mt-8 space-y-10">
                <div className="rounded-full bg-[#f3f2ff] px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Search className="h-5 w-5 text-[#5757fd]" />
                    <Input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Zoek evenementen"
                      className="border-none bg-transparent p-0 text-[#5757fd] shadow-none outline-none placeholder:text-[#5757fd] focus:border-none focus:outline-none focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                    />
                  </div>
                </div>

                <FilterGroup
  title="Type"
  options={types}
  selected={selectedTypes}
  setSelected={setSelectedTypes}
/>

<FilterGroup
  title="Datum"
  options={dates}
  selected={selectedDates}
  setSelected={setSelectedDates}
/>

<FilterGroup
  title="Locatie"
  options={locations}
  selected={selectedLocations}
  setSelected={setSelectedLocations}
/>

                <Button
                  onClick={clearFilters}
                  variant="outline"
                  className="w-full rounded-full border-[#5757fd] text-[#5757fd] hover:bg-[#5757fd] hover:border-[#5757fd]"
                >
                  Reset filters
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>

        {hasActiveFilters && (
          <div className="mb-4 flex flex-wrap items-center gap-2">
            {[...selectedTypes, ...selectedDates, ...selectedLocations].map((filter) => (
  <span
    key={filter}
    className="rounded-full bg-white px-4 py-2 text-sm font-bold font-sans_bold"
  >
    {getFilterLabel(filter)}
  </span>
))}

            {search && (
              <span className="rounded-full bg-white px-4 py-2 text-sm font-bold">
                Zoekterm: {search}
              </span>
            )}

            <Button
              onClick={clearFilters}
              variant="outline"
              className="px-4 py-2 h-fit text-sm rounded-full bg-[#5757fd] text-white hover:bg-[#5757fd]/80 no-border hover:no-border hover:border-0 border-0 font-sans"
            >
              <X className='w-4 h-4 mr-2' /> Reset filters
            </Button>
          </div>
        )}

        {regularEvents.length > 0 ? (
          <div className="columns-1 gap-4 sm:columns-2 lg:columns-4">
            {regularEvents.map((event) => (
              <div key={event._id} className="mb-4 break-inside-avoid">
                <EventCard event={event} />
              </div>
            ))}
          </div>
        ) : (
          <div className="rounded-2xl bg-white/40 p-24 text-center">
            <p className="mb-6 text-xl font-sans">Geen evenementen gevonden.</p>
            <Button
              onClick={clearFilters}
              className="px-8 py-6 rounded-full bg-[#5757fd] text-white hover:bg-[#4444e8]"
            >
              Reset filters
            </Button>
          </div>
        )}
      </section>
    </main>
  )
}

export default HomePage